import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Loader } from 'lucide-react';
import SearchableDropdown from '../common/SearchableDropdown';
import NumericInput from '../common/NumericInput';
import DatePicker from '../common/DatePicker';
import ConfirmationModal from '../common/ConfirmationModal';
import { adminService } from '../../services/api';
import { invoiceService } from '../../services/modules/health';
import { salesInvoiceService } from '../../services/modules/inventory';
import { useToast } from '../../context/ToastContext';
import { useConfirmation } from '../../hooks/useConfirmation';
import { toSafeFixed } from '../../utils/numberUtils';

interface CommissionItem {
  item_type: string;
  item_id: number;
  item_name: string;
  item_rate: number;
  commission_percentage: number;
  commission_value: number;
  gst_percentage: number;
  gst_amount: number;
  cess_percentage: number;
  cess_amount: number;
  total_amount: number;
  discount_percentage: number;
  discount_amount: number;
  roundoff: number;
  final_amount: number;
}

interface AgencyCommissionFormProps {
  commission?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetForm: boolean;
}

const AgencyCommissionForm: React.FC<AgencyCommissionFormProps> = ({
  commission,
  onSave,
  onCancel,
  isCollapsed,
  onToggleCollapse,
  resetForm
}) => {
  const [formData, setFormData] = useState({
    order_commission_number: '',
    order_type: 'TEST',
    order_id: '',
    order_number: '',
    invoice_id: '',
    invoice_number: '',
    agency_id: '',
    agency_name: '',
    agency_phone: '',
    notes: '',
    total_amount: 0,
    disc_percentage: 0,
    disc_amount: 0,
    roundoff: 0,
    final_amount: 0,
    // Payment fields
    payment_amount: 0,
    payment_mode: 'CASH',
    instrument_number: '',
    instrument_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    branch_name: '',
    ifsc_code: '',
    transaction_reference: '',
    payment_remarks: ''
  });

  const [items, setItems] = useState<CommissionItem[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [agencies, setAgencies] = useState<any[]>([]);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const { showToast } = useToast();
  const { confirmationState, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();

  useEffect(() => {
    loadAgencies();
    loadInvoiceTypes();
    // Load invoices for the initial order type
    loadInvoices('TEST');
  }, []);

  useEffect(() => {
    if (formData.order_type) {
      console.log('Order type changed to:', formData.order_type);
      loadInvoices(formData.order_type);
    }
  }, [formData.order_type]);

  useEffect(() => {
    if (commission) {
      setFormData({
        order_commission_number: commission.order_commission_number || '',
        order_type: commission.order_type || 'TEST',
        order_id: commission.order_id || '',
        order_number: commission.order_number || '',
        invoice_id: commission.invoice_id || '',
        invoice_number: commission.invoice_number || '',
        agency_id: commission.agency_id || '',
        agency_name: commission.agency_name || '',
        agency_phone: commission.agency_phone || '',
        notes: commission.notes || '',
        total_amount: commission.total_amount || 0,
        disc_percentage: commission.disc_percentage || 0,
        disc_amount: commission.disc_amount || 0,
        roundoff: commission.roundoff || 0,
        final_amount: commission.final_amount || 0,
        // Payment fields
        payment_amount: commission.payment?.payment_amount || 0,
        payment_mode: commission.payment?.payment_mode || 'CASH',
        instrument_number: commission.payment?.instrument_number || '',
        instrument_date: commission.payment?.instrument_date || new Date().toISOString().split('T')[0],
        bank_name: commission.payment?.bank_name || '',
        branch_name: commission.payment?.branch_name || '',
        ifsc_code: commission.payment?.ifsc_code || '',
        transaction_reference: commission.payment?.transaction_reference || '',
        payment_remarks: commission.payment?.payment_remarks || ''
      });
      setItems(commission.items || []);
    } else if (resetForm) {
      setFormData({
        order_commission_number: '',
        order_type: 'TEST',
        order_id: '',
        order_number: '',
        invoice_id: '',
        invoice_number: '',
        agency_id: '',
        agency_name: '',
        agency_phone: '',
        notes: '',
        total_amount: 0,
        disc_percentage: 0,
        disc_amount: 0,
        roundoff: 0,
        final_amount: 0,
        // Payment fields
        payment_amount: 0,
        payment_mode: 'CASH',
        instrument_number: '',
        instrument_date: new Date().toISOString().split('T')[0],
        bank_name: '',
        branch_name: '',
        ifsc_code: '',
        transaction_reference: '',
        payment_remarks: ''
      });
      setItems([]);
    }
  }, [commission, resetForm]);

  const loadAgencies = async () => {
    try {
      const response = await adminService.getAgencies({ page: 1, per_page: 100 });
      setAgencies(response.data);
    } catch (error) {
      showToast('error', 'Failed to load agencies');
    }
  };

  const loadInvoiceTypes = async () => {
    setInvoiceTypes([
      { value: 'TEST', label: 'Test Invoice' },
      { value: 'CLINIC', label: 'Clinic Invoice' },
      { value: 'PRODUCT', label: 'Product Invoice' }
    ]);
  };

  const loadInvoices = async (invoiceType: string) => {
    try {
      setInvoices([]); // Clear previous invoices
      let response;
      
      if (invoiceType === 'TEST') {
        response = await invoiceService.getTestInvoices({ per_page: 100 });
      } else if (invoiceType === 'CLINIC') {
        response = await invoiceService.getTestInvoices({ per_page: 100 });
      } else if (invoiceType === 'PRODUCT') {
        response = await salesInvoiceService.getSalesInvoices({ page_size: 100 });
      }
      
      console.log('Full response:', response);
      const invoiceData = response?.data || [];
      console.log('Extracted invoice data:', invoiceData);
      console.log('Number of invoices:', invoiceData.length);
      
      if (Array.isArray(invoiceData)) {
        setInvoices(invoiceData);
      } else {
        console.error('Invoice data is not an array:', invoiceData);
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      showToast('error', 'Failed to load invoices');
    }
  };

  const handleInvoiceChange = async (value: string | number | (string | number)[]) => {
    const invoiceId = Array.isArray(value) ? Number(value[0]) : Number(value);
    const selectedInvoice = invoices.find(i => i.id === invoiceId);

    if (!selectedInvoice) {
      return;
    }

    setLoadingInvoice(true);
    try {
      // Fetch full invoice details
      let invoiceDetails;
      if (formData.order_type === 'TEST') {
        invoiceDetails = await invoiceService.getTestInvoice(invoiceId);
      } else if (formData.order_type === 'CLINIC') {
        invoiceDetails = await invoiceService.getTestInvoice(invoiceId);
      } else if (formData.order_type === 'PRODUCT') {
        invoiceDetails = await salesInvoiceService.getSalesInvoice(invoiceId);
      }

      const invoice = invoiceDetails?.data || selectedInvoice;

      // Extract order information from invoice
      const orderNumber = invoice.order?.test_order_number || invoice.order?.order_number || '';
      const orderId = invoice.order_id || '';

      // Get agency information
      const selectedAgency = agencies.find(a => a.id === invoice.agency_id);

      // Extract items from invoice
      const commissionItems: CommissionItem[] = (invoice.items || []).map((item: any) => ({
        item_type: item.item_type || (formData.order_type === 'TEST' ? 'TEST' : formData.order_type),
        item_id: item.test_id || item.product_id || item.id || 0,
        item_name: item.test_name || item.product_name || item.name || '',
        item_rate: item.rate || item.price || 0,
        commission_percentage: 0,
        commission_value: 0,
        gst_percentage: item.gst_rate || 0,
        gst_amount: item.gst_amount || 0,
        cess_percentage: item.cess_rate || 0,
        cess_amount: item.cess_amount || 0,
        total_amount: item.total_amount || 0,
        discount_percentage: item.disc_percentage || 0,
        discount_amount: item.disc_amount || 0,
        roundoff: 0,
        final_amount: item.total_amount || 0
      }));

      setFormData(prev => ({
        ...prev,
        invoice_id: String(invoiceId),
        invoice_number: invoice.invoice_number || '',
        order_id: String(orderId),
        order_number: orderNumber,
        agency_id: String(invoice.agency_id || ''),
        agency_name: selectedAgency?.name || '',
        agency_phone: selectedAgency?.phone || '',
        total_amount: invoice.total_amount || 0,
        disc_percentage: invoice.disc_percentage || 0,
        disc_amount: invoice.disc_amount || 0,
        roundoff: invoice.roundoff || 0,
        final_amount: invoice.final_amount || 0
      }));

      setItems(commissionItems);
    } catch (error) {
      showToast('error', 'Failed to load invoice details');
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleItemCommissionChange = (index: number, commissionPercentage: number) => {
    const updatedItems = [...items];
    const item = updatedItems[index];
    
    item.commission_percentage = commissionPercentage;
    item.commission_value = (item.item_rate * commissionPercentage) / 100;
    item.final_amount = parseFloat(toSafeFixed(item.total_amount + item.commission_value, 2));
    
    setItems(updatedItems);
  };

  const handlePaymentModeChange = (mode: string) => {
    setFormData(prev => ({
      ...prev,
      payment_mode: mode,
      instrument_number: '',
      instrument_date: new Date().toISOString().split('T')[0],
      bank_name: '',
      branch_name: '',
      ifsc_code: '',
      transaction_reference: ''
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.invoice_id || !formData.agency_id) {
      showToast('error', 'Invoice and Agency are required');
      return;
    }
    if (items.length === 0) {
      showToast('error', 'Please select an invoice with items');
      return;
    }

    const payload = {
      order_commission_number: formData.order_commission_number,
      order_type: formData.order_type,
      commission_type: 'Invoice', // Default commission type
      order_id: Number(formData.order_id),
      order_number: formData.order_number,
      invoice_id: Number(formData.invoice_id),
      invoice_number: formData.invoice_number,
      agency_id: Number(formData.agency_id),
      agency_name: formData.agency_name,
      agency_phone: formData.agency_phone,
      invoice_final_bill_amount: formData.final_amount, // Use final amount as invoice final bill amount
      notes: formData.notes,
      total_amount: formData.total_amount,
      disc_percentage: formData.disc_percentage,
      disc_amount: formData.disc_amount,
      roundoff: formData.roundoff,
      final_amount: formData.final_amount,
      items: items.map(item => ({
        item_type: item.item_type,
        item_id: item.item_id,
        item_name: item.item_name,
        item_rate: item.item_rate,
        commission_percentage: item.commission_percentage,
        commission_value: item.commission_value,
        gst_percentage: item.gst_percentage,
        gst_amount: item.gst_amount,
        cess_percentage: item.cess_percentage,
        cess_amount: item.cess_amount,
        total_amount: item.total_amount,
        discount_percentage: item.discount_percentage,
        discount_amount: item.discount_amount,
        roundoff: item.roundoff,
        final_amount: item.final_amount
      })),
      payment: {
        payment_number: `PAY-${Date.now()}`, // Generate a payment number
        payment_amount: formData.payment_amount,
        payment_mode: formData.payment_mode,
        instrument_number: formData.instrument_number,
        instrument_date: formData.instrument_date,
        bank_name: formData.bank_name,
        branch_name: formData.branch_name,
        ifsc_code: formData.ifsc_code,
        transaction_reference: formData.transaction_reference,
        payment_remarks: formData.payment_remarks
      }
    };

    onSave(payload);
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {commission ? 'Edit Agency Commission' : 'Add New Agency Commission'}
        </h2>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="text-gray-500 hover:text-gray-700"
        >
          {isCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
        </button>
      </div>

      {!isCollapsed && (
        <form onSubmit={handleSubmit} className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agency *</label>
              <SearchableDropdown
                options={agencies.map(a => ({ value: a.id, label: a.name }))}
                value={formData.agency_id ? Number(formData.agency_id) : ''}
                onChange={(value) => setFormData({ ...formData, agency_id: String(Array.isArray(value) ? value[0] : value) })}
                placeholder="Select Agency"
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Type *</label>
              <SearchableDropdown
                options={invoiceTypes}
                value={formData.order_type}
                onChange={(value) => setFormData({ ...formData, order_type: String(Array.isArray(value) ? value[0] : value), invoice_id: '', invoice_number: '', order_id: '', order_number: '' })}
                placeholder="Select Invoice Type"
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice *</label>
              <SearchableDropdown
                options={invoices && invoices.length > 0 ? invoices.map(i => ({ value: i.id, label: `${i.invoice_number}` })) : []}
                value={formData.invoice_id ? Number(formData.invoice_id) : ''}
                onChange={handleInvoiceChange}
                placeholder={invoices.length === 0 ? "No invoices available" : "Select Invoice"}
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number</label>
              <input
                type="text"
                value={formData.invoice_number}
                readOnly
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Order Number</label>
              <input
                type="text"
                value={formData.order_number}
                readOnly
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agency Name</label>
              <input
                type="text"
                value={formData.agency_name}
                readOnly
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agency Phone</label>
              <input
                type="text"
                value={formData.agency_phone}
                readOnly
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Total Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.total_amount}
                readOnly
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={1}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary resize-none overflow-y-auto"
                style={{ minHeight: '30px', maxHeight: '30px' }}
              />
            </div>
          </div>

          {/* Items Table */}
          {items.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold mb-2">Commission Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="px-2 py-1 text-left">Item Name</th>
                      <th className="px-2 py-1 text-right">Item Rate</th>
                      <th className="px-2 py-1 text-right">Total Amount</th>
                      <th className="px-2 py-1 text-right">Commission %</th>
                      <th className="px-2 py-1 text-right">Commission Value</th>
                      <th className="px-2 py-1 text-right">GST %</th>
                      <th className="px-2 py-1 text-right">GST Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1">{item.item_name}</td>
                        <td className="px-2 py-1 text-right">{toSafeFixed(item.item_rate, 2)}</td>
                        <td className="px-2 py-1 text-right">{toSafeFixed(item.total_amount, 2)}</td>
                        <td className="px-2 py-1 text-right">
                          <input
                            type="number"
                            step="0.01"
                            value={item.commission_percentage}
                            onChange={(e) => handleItemCommissionChange(idx, parseFloat(e.target.value) || 0)}
                            className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </td>
                        <td className="px-2 py-1 text-right">{toSafeFixed(item.commission_value, 2)}</td>
                        <td className="px-2 py-1 text-right">{toSafeFixed(item.gst_percentage, 2)}</td>
                        <td className="px-2 py-1 text-right">{toSafeFixed(item.gst_amount, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment Information Section */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold mb-2">Payment Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount</label>
                <NumericInput
                  value={formData.payment_amount}
                  onChange={(value) => setFormData(prev => ({ ...prev, payment_amount: value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                  step={0.01}
                  min={0}
                  allowNegative={false}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode</label>
                <SearchableDropdown
                  options={[
                    { value: 'CASH', label: 'Cash' },
                    { value: 'CHEQUE', label: 'Cheque' },
                    { value: 'CARD', label: 'Card' },
                    { value: 'UPI', label: 'UPI' },
                    { value: 'BANK_TRANSFER', label: 'Bank Transfer' }
                  ]}
                  value={formData.payment_mode}
                  onChange={(v) => handlePaymentModeChange(v.toString())}
                  placeholder="Select mode"
                  multiple={false}
                  searchable={false}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Remarks</label>
                <input
                  type="text"
                  value={formData.payment_remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_remarks: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                />
              </div>

              {/* Payment Mode Specific Fields */}
              {(() => {
                const fields = [];

                // Instrument Number - shown for CHEQUE, CARD, BANK_TRANSFER
                if (['CHEQUE', 'CARD', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="instrument_number">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Instrument Number</label>
                      <input
                        type="text"
                        value={formData.instrument_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, instrument_number: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                // Instrument Date - shown for CHEQUE, CARD, UPI, BANK_TRANSFER
                if (['CHEQUE', 'CARD', 'UPI', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="instrument_date">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Instrument Date</label>
                      <DatePicker
                        value={formData.instrument_date}
                        onChange={(v) => setFormData(prev => ({ ...prev, instrument_date: v }))}
                      />
                    </div>
                  );
                }

                // Transaction Reference - shown for UPI
                if (formData.payment_mode === 'UPI') {
                  fields.push(
                    <div key="transaction_reference">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Reference</label>
                      <input
                        type="text"
                        value={formData.transaction_reference}
                        onChange={(e) => setFormData(prev => ({ ...prev, transaction_reference: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                // Bank Name - shown for CHEQUE, BANK_TRANSFER
                if (['CHEQUE', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="bank_name">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={formData.bank_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                // Branch Name - shown for CHEQUE, BANK_TRANSFER
                if (['CHEQUE', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="branch_name">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Branch Name</label>
                      <input
                        type="text"
                        value={formData.branch_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, branch_name: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                // IFSC Code - shown for CHEQUE, BANK_TRANSFER
                if (['CHEQUE', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="ifsc_code">
                      <label className="block text-xs font-medium text-gray-700 mb-1">IFSC Code</label>
                      <input
                        type="text"
                        value={formData.ifsc_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, ifsc_code: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                return fields;
              })()}
            </div>
          </div>

          {loadingInvoice && (
            <div className="flex items-center justify-center py-4">
              <Loader className="h-5 w-5 animate-spin mr-2" />
              <span>Loading invoice details...</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 mt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loadingInvoice}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-secondary rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingInvoice ? (
                <>
                  <Loader className="h-3 w-3 animate-spin inline mr-1" />
                  Loading...
                </>
              ) : (
                commission ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </form>
      )}
      
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        variant={confirmationState.variant}
        onConfirm={handleConfirm}
        onCancel={hideConfirmation}
      />
    </div>
  );
};

export default AgencyCommissionForm;
